import React from 'react';
import PropTypes from 'prop-types';
import Headline from '../shared/Headline';
import styled from 'styled-components';
import SignUpStep from './SignUpStep';
import { FormattedMessage } from 'react-intl';
import FirstName from '../fields/FirstName';
import LastName from '../fields/LastName';
import PhoneNumber from '../fields/PhoneNumber';
import Industry from '../fields/Industry';
import HowHeard from '../fields/HowHeard';
import CompanySize from '../fields/CompanySize';
import { useFormikContext } from 'formik';
import isMobile from 'helpers/isMobile';
import PrimaryUserTitle from '../fields/PrimaryUserTitle';
import { Button } from '@companycam/slab-web';

const MyProfileTitle = (
  <FormattedMessage
    id="Onboard.myProfile.title"
    description="Title for My Profile page"
    defaultMessage="Take a second to finish up your profile."
  />
);

const MyProfileTitleCollab = (
  <FormattedMessage
    id="Onboard.myProfile.titleCollab"
    description="Title for My Profile page when user is signing up from a collab invite"
    defaultMessage="Take a second to finish up your profile. Then get right to work on the project!"
  />
);

const ButtonText = (
  <FormattedMessage
    id="Onboard.signUp.button"
    description="Button text on sign up page"
    defaultMessage="Continue"
  />
);

const CreateProfileButtonText = (
  <FormattedMessage
    id="Onboard.myProfile.createProfile"
    description="Button text on My Profile page"
    defaultMessage="Create Profile"
  />
);

// #region styled-components
const ContentView = styled.div`
  flex-grow: 1;
`;

const ButtonView = styled.div`
  padding-top: 8px;
`;

const FieldView = styled.div`
  margin-top: 8px;
`;

const NameView = styled.div`
  display: flex;
  margin-top: 24px;
  flex-direction: row;
  justify-content: space-between;
`;
const NameInputView = styled.div`
  width: 49%;
`;
// #endregion

const MyProfile = ({
  currentStepName,
  stepName,
  buttonAction,
  disabled,
  projectInviteToken,
  requireCompanySize,
}) => {
  const { values, errors } = useFormikContext();

  if (projectInviteToken) {
    // suppress howHeard if we're collaborating
    values.howHeardType = 'project_collaboration';
    values.howHeardDetail = 'Project Collaboration Invite';
  }

  const relevantErrors = [
    'firstName',
    'LastName',
    'phoneNumber',
    'industry',
    'howHeardType',
    'howHeardDetail',
    'companySize',
    'primaryUserTitle',
  ];

  const errorsVisible = relevantErrors.some((field) => errors[field]);

  return (
    <SignUpStep
      currentStepName={currentStepName}
      stepName={stepName}
      buttonAction={buttonAction}
      disabled={disabled}
    >
      <ContentView contentContainerStyle={{ flexGrow: 1 }}>
        <Headline
          text={projectInviteToken ? MyProfileTitleCollab : MyProfileTitle}
        />
        <NameView>
          <NameInputView>
            <FirstName />
          </NameInputView>
          <NameInputView>
            <LastName />
          </NameInputView>
        </NameView>
        <PhoneNumber />
        <FieldView>
          {!isMobile && <Industry />}
          <PrimaryUserTitle />
          {requireCompanySize && <CompanySize />}
          {!projectInviteToken && <HowHeard />}
        </FieldView>
        <ButtonView>
          <Button
            color="primary"
            style={{ width: '100%' }}
            data-testid="create-account-button"
            onClick={buttonAction}
            disabled={disabled || errorsVisible}
            type="submit"
          >
            {isMobile ? CreateProfileButtonText : ButtonText}
          </Button>
        </ButtonView>
      </ContentView>
    </SignUpStep>
  );
};

MyProfile.propTypes = {
  currentStepName: PropTypes.string,
  stepName: PropTypes.string,
  buttonAction: PropTypes.func,
  disabled: PropTypes.bool,
  projectInviteToken: PropTypes.string,
  requireCompanySize: PropTypes.bool,
};

export default MyProfile;
